import { useColaboradores } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import type { Colaborador } from "../types/cms";

/**
 * Banda de logos de colaboradores y patrocinadores.
 *
 * Lee la colección Colaborador del CMS, ordenada por el campo `orden`
 * ascendente (los logos institucionales prioritarios deben tener un valor
 * de orden bajo — ej. UEx orden=1, ayuntamientos orden=10, fundaciones
 * orden=20...). Si la entrada del CMS tiene `web`, el logo se envuelve en
 * un enlace con `aria-label` accesible.
 *
 * Empty state: si la colección está vacía o no hay permisos, se renderiza
 * `null` para no mostrar una sección vacía.
 *
 * Accesibilidad:
 *   - `<section aria-labelledby>` para que los lectores anuncien el bloque.
 *   - Cada logo lleva `alt` con el nombre del colaborador.
 *   - Los enlaces externos llevan `rel="noopener noreferrer"` + `target="_blank"`.
 */
export function PartnersStrip() {
  const { data, isLoading } = useColaboradores();

  if (isLoading) return null;
  if (!data || data.length === 0) return null;

  return (
    <section
      aria-labelledby="colaboradores-heading"
      className="border-t border-gray-200 bg-white py-16"
    >
      <div className="container-emjc">
        <div className="mb-10 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
            Con el apoyo de
          </p>
          <h2
            id="colaboradores-heading"
            className="mt-2 font-display text-3xl font-semibold md:text-4xl"
          >
            Instituciones y entidades colaboradoras
          </h2>
        </div>

        <ul className="grid grid-cols-2 items-center justify-items-center gap-x-8 gap-y-10 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {data.map((c) => (
            <li key={c.id} className="flex h-20 w-full items-center justify-center">
              <PartnerLogo colaborador={c} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PartnerLogo({ colaborador }: { colaborador: Colaborador }) {
  const src = mediaUrl(colaborador.logo?.url);
  if (!src) return null;

  const img = (
    <img
      src={src}
      alt={colaborador.nombre}
      loading="lazy"
      className="max-h-16 max-w-[140px] object-contain opacity-90 transition-opacity duration-200 hover:opacity-100"
    />
  );

  if (colaborador.web) {
    return (
      <a
        href={colaborador.web}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${colaborador.nombre} (abre en una pestaña nueva)`}
        className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2"
      >
        {img}
      </a>
    );
  }

  return img;
}
